import React, { useMemo } from "react";

import Form from "antd/es/form";
import { useWatch } from "antd/es/form/Form";
import { defineMessages, useIntl } from "react-intl";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { SelectField } from "@mapmycustomers/ui";

import CardType from "@app/enum/dashboard/CardType";
import SourceType, { getSourceTypeDisplayName, SupportedSources } from "@app/enum/SourceType";
import CardConfigurationProps from "@app/scene/dashboard/types/CardConfigurationProps";
import EntityTypesSupportedInNoContactIn from "@app/types/dashboard/cards/noContactIn/EntityTypesSupportedInNoContactIn";
import FrequencyStatusType, {
  getFrequencyStatusTypeDisplayName,
} from "@app/types/dashboard/FrequencyStatusType";
import universalFieldsFieldModel, {
  UniversalFieldName,
} from "@app/util/fieldModel/universalFieldsFieldModel";
import { getEntityTypeDisplayName } from "@app/util/ui";

import CriteriaExtraMapFilters from "../common/CriteriaExtraMapFilters";

import { NO_CONTACT_IN_ENTITY_TYPES } from "./consts";

const messages = defineMessages({
  frequencyStatus: {
    id: "dashboard.editModal.criteria.noContactIn.frequencyStatus",
    defaultMessage: "Frequency Status",
    description:
      "Title of the Frequency Status field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  frequencyStatusPlaceholder: {
    id: "dashboard.editModal.criteria.noContactIn.frequencyStatus.placeholder",
    defaultMessage: "Any Value",
    description:
      "Placeholder of the Frequency Status field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  recordTypes: {
    id: "dashboard.editModal.criteria.noContactIn.entityTypes",
    defaultMessage: "Record Type(s)",
    description:
      "Title of the Record Types field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  recordTypesPlaceholder: {
    id: "dashboard.editModal.criteria.noContactIn.entityTypes.placeholder",
    defaultMessage: "Any Record Type",
    description:
      "Placeholder of the Record Types field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  sources: {
    id: "dashboard.editModal.criteria.noContactIn.sources",
    defaultMessage: "Source(s)",
    description:
      "Title of the Sources field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  sourcesPlaceholder: {
    id: "dashboard.editModal.criteria.noContactIn.sources.placeholder",
    defaultMessage: "Any Source",
    description:
      "Placeholder of the Sources field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
});

const hiddenExtraFilterFields = [
  UniversalFieldName.SOURCE,
  UniversalFieldName.TEAM,
  UniversalFieldName.USER,
  UniversalFieldName.CREATOR,
  UniversalFieldName.FREQUENCY_STATUS,
  UniversalFieldName.NO_CONTACT_IN,
  UniversalFieldName.VIEW_AS,
];

interface Props extends CardConfigurationProps {}

const CriteriaFormComponent: React.FC<Props> = ({ disabled }) => {
  const intl = useIntl();

  const entityTypes: EntityTypesSupportedInNoContactIn[] | undefined = useWatch([
    "criteria",
    "entityTypes",
  ]);

  const entityTypeOptions = useMemo(
    () =>
      NO_CONTACT_IN_ENTITY_TYPES.map((entityType) => ({
        label: getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true }),
        value: entityType,
      })),
    [intl]
  );

  const sourceOptions = useMemo(
    () =>
      Object.values(SourceType).map((value) => ({
        label: getSourceTypeDisplayName(intl, value),
        value,
      })),
    [intl]
  );

  const frequencyStatusOptions = useMemo(
    () =>
      Object.values(FrequencyStatusType).map((value) => ({
        label: getFrequencyStatusTypeDisplayName(intl, value),
        value,
      })),
    [intl]
  );

  const hiddenFields = useMemo(
    () =>
      hiddenExtraFilterFields
        .map((fieldName) => universalFieldsFieldModel.getByName(fieldName))
        .filter(isDefined),
    []
  );

  return (
    <div>
      <Form.Item
        label={intl.formatMessage(messages.recordTypes)}
        name={["criteria", "entityTypes"]}
      >
        <SelectField<EntityTypesSupportedInNoContactIn>
          allowClear
          locked={disabled}
          mode="multiple"
          options={entityTypeOptions}
          placeholder={intl.formatMessage(messages.recordTypesPlaceholder)}
          showArrow
        />
      </Form.Item>
      <Form.Item label={intl.formatMessage(messages.sources)} name={["criteria", "sources"]}>
        <SelectField<SupportedSources>
          allowClear
          locked={disabled}
          mode="multiple"
          options={sourceOptions}
          placeholder={intl.formatMessage(messages.sourcesPlaceholder)}
          showArrow
        />
      </Form.Item>
      <Form.Item
        label={intl.formatMessage(messages.frequencyStatus)}
        name={["criteria", "frequencyStatus"]}
      >
        <SelectField<FrequencyStatusType>
          allowClear
          locked={disabled}
          mode="multiple"
          options={frequencyStatusOptions}
          placeholder={intl.formatMessage(messages.frequencyStatusPlaceholder)}
          showArrow
        />
      </Form.Item>
      <Form.Item name="extraFilters">
        {entityTypes && (
          <CriteriaExtraMapFilters
            cardType={CardType.NO_CONTACT_IN}
            entityTypes={entityTypes}
            hiddenFields={hiddenFields}
          />
        )}
      </Form.Item>
    </div>
  );
};

export default CriteriaFormComponent;
